<template>
  <div id="map-widget" ref="widget" :class="{ iframe, mobile }" :style="{ height }">
    <MapBox
      class="MapBox__wrapper"
      aria-hidden="true"
      :style="{ left: popupVisible ? '275px' : 0 }" 
      :accessToken="accessToken"
      :mapStyle.sync="mapStyle"
      :options="options"
      @load="onMapLoaded" />

    <LegendCtrl
      aria-hidden="true"
      v-if="legendItems"
      @open="handleOpenLegend" />
    <Legend 
      v-if="legendItems"
      aria-hidden="true"
      :items="legendItems"
      :visible="legendVisible" 
      :mobile="mobile"
      @close="handleCloseLegend" />
    <Popup 
      aria-hidden="true"
      :visible="popupVisible" 
      :title="popupTitle" 
      :content="popupContent" 
      :width="popupWidth"
      :mobile="mobile"
      @close="handleClosePopup" /> 

    <div v-if="screenreader" class="ScreenReader sr-only" v-html="screenreader" role="region"></div>
  </div>
</template>

<script>

import Legend from './components/Legend.vue'
import LegendCtrl from './components/LegendCtrl.vue'
import MapBox from './components/MapBox.vue'
import Popup from './components/Popup'

import mapboxgl from 'mapbox-gl'
import screenfull from 'screenfull';
import { ResizeObserver } from '@juggle/resize-observer';

// Map Configs
import { LayerConfigPerMap, ContentConfigPerMap } from './config'

/**
 * The MapBox instance must not be reactive
 */
let map;

export default {
  name: 'App',
  components: {
    Legend, LegendCtrl, MapBox, Popup
  },
  props: {
    /**
     * CSS styling flag
     */
    iframe: {
      type: Boolean,
      default: false
    },
    /**
     * The height of the widget. Ignored in iframe mode
     */
    height: {
      type: String,
      default: '400px'
    },
    /**
     * Determines which map is being shown
     *  Possible options depend on the available config
     */
    kaart: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      // Mapbox settings & status
      accessToken: process.env.VUE_APP_MAPBOX_TOKEN,
      mapStyle: process.env.VUE_APP_MAPBOX_STYLE,
      options: {
        zoom: process.env.VUE_APP_MAPBOX_ZOOM,
        center: [
          process.env.VUE_APP_MAPBOX_LONG,
          process.env.VUE_APP_MAPBOX_LAT
        ]
      },
      loaded: true,

      // Legend
      legendVisible: true,

      // Popup properties
      popupVisible: false,
      popupTitle: '',
      popupContent: '',

      // Size event observer
      ResizeObserver: null,

      // Repaint timeout instance
      repaintTimeout: null,

      // Hover & active layer state. May be used by a layer config
      hoverStateId: null,
      activeStateId: null,

      // Set to true if the widget width is below 375px
      mobile: false
    }
  },
  computed: {
    /**
     * The active map can be specified through the 'kaart' prop. However, if there is no config available for the 
     * requested map, the prop is ignored and the first configured map is shown instead
     */
    activeMap() {
      // Check whether the layer config is available for the requested map. If not, default to the first layer config
      let MapLayerConfigNames = Object.keys(LayerConfigPerMap)

      return MapLayerConfigNames.includes(this.kaart.toLowerCase())
        ? this.kaart.toLowerCase()
        : MapLayerConfigNames[0]
    },
    /**
     * Obtain the screenreader html for the active map. 
     * Returns false if there is no screenreader html, or no content at all
     */
    screenreader() {
      return ContentConfigPerMap[this.activeMap] 
        ? ContentConfigPerMap[this.activeMap].screenreader
        : false
    },
    /**
     * The legend items configuration. 
     */
    legendItems() {
      return ContentConfigPerMap[this.activeMap] 
        ? ContentConfigPerMap[this.activeMap].legend
        : false
    },
    popupWidth() {
      return ContentConfigPerMap[this.activeMap]
        ? ContentConfigPerMap[this.activeMap].popupWidth + 'px'
        : '275px'
    }
  },
  watch: {
    activeMap() {
      // TODO: Listen to changes in the requested map. 
      // TODO: Clean up event listeners, remove layers
      // TODO: Add new layers, add new event listeners
    },
    activeStateId(value) {
      this.$emit('active-change', value)
    },
    hoverStateId(value) {
      this.$emit('hover-change', value)
    }
  },
  mounted() {
    // Detect transition to/from fullscreen
    screenfull.on('change', this.handleFullscreenChange);

    // Detect window resize events that impact the widget
    // Regular responsive CSS is not useable in a widget context
    this.ResizeObserver = new ResizeObserver(this.handleResize)
    this.ResizeObserver.observe(this.$refs.widget)

    // Do a widget size check upon mounting
    this.handleResize()

    // Normally we show the legend on opening, unless we're opening in mobile mode
    if (this.mobile) {
      this.legendVisible = false
    }

    // mapboxgl.clearStorage(); // For development purposes. Clears mapbox cache
  },
  /**
   * Clear event handlers
   */
  beforeDestroy() {
    screenfull.off('change', this.handleFullscreenChange)

    this.ResizeObserver.disconnect()

    this.$emit('beforeDestroy')
  },
  methods: {

    onMapLoaded(event) {
      // Note: a reference to the map has to be stored in a non-reactive manner.
      map = event.map 

      this.addMapControls()
      this.addLayers()

      this.loaded = true
    },

    /**
     * Add the control elements to the MapBox instance
     */
    addMapControls() {
      let nav = new mapboxgl.NavigationControl({
        showCompass: false,
        showZoom: true
      })
      map.addControl(new mapboxgl.FullscreenControl({
        container: this.$refs.widget
      }), 'top-right')
      map.addControl(nav, 'top-right');
      
      map.addControl(new mapboxgl.ScaleControl(), 'bottom-left');
    },

    /**
     * Apply the layer configs for the active map
     */
    addLayers() {
      let firstSymbolId = this.getFirstSymbolLayerId()
      let layerConfig = LayerConfigPerMap[this.activeMap]

      /**
       * Go over each layer in the map config
       */
      layerConfig.forEach(layer => {

        if (layer.source) {
          // Add source based on layer type
          if (layer.source.type === 'geojson') {
            map.addSource(layer.id, {
              type: 'geojson',
              data: layer.source.data
            })
          } else if (layer.source.type === 'vector') {
            map.addSource(layer.id, {
              type: 'vector',
              url: layer.source.url
            })
          }
        }

        // If the layer's source was added, add the layer (this is independent from the source details above)
        if (layer.spec && (map.getSource(layer.spec.source) || map.getSource(layer.id))) {
          // If neither the layer id or source is defined, use the id
          let spec = Object.assign(
            {
              id: layer.id,
              source: layer.id
            },
            layer.spec
          )
          if (layer.labels) {
            map.addLayer(spec)
          } else {
            map.addLayer(spec, firstSymbolId)
          }
        }
      

        /**
         * Setup layer interactions
         */
        if (layer.setupInteraction) {
          layer.setupInteraction.call(this, { map })
        }

        /**
         * Enable popup interaction if a popup property was specified.
         */
        if (layer.popupProperty) {
          this.enablePopupInteraction({
            layerId: layer.id,
            property: layer.popupProperty,
            content: ContentConfigPerMap[this.activeMap].content
          })
        }
      })
    },

    // We want to add our layers below the labels (e.g. city names)
    // https://docs.mapbox.com/mapbox-gl-js/example/geojson-layer-in-stack/
    getFirstSymbolLayerId() {
      let layers = map.getStyle().layers;
      // Find the index of the first symbol layer in the map style
      let firstSymbolId;
      for (let i = 0; i < layers.length; i++) {
        if (layers[i].type === 'symbol') {
          firstSymbolId = layers[i].id;
          break;
        }
      }

      return firstSymbolId
    },

    /**
     * Setup Popup interaction
     */
    enablePopupInteraction({ layerId, property, content }) {      
      /**
       * Popup - interaction
       */
      map.on('click', layerId, (e) => {

        if (e.features.length === 0 || e._defaultPrevented) {
          return 
        }
        if (! e.features[0].properties[`${property}`]) {
          return
        }

        let data = content[e.features[0].properties[`${property}`]]

        if (data && data.title && data.content) {
          this.showPopup({
            eventData: e.features[0].properties,
            title: data.title,
            content: data.content
          })
        }
      })
      this.$on('popup-open', ({ properties }) => {
        if (properties[`${property}`]) {
          this.activeStateId = properties[`${property}`]
        }
      })
      this.$on('popup-close', () => {
        this.activeStateId = null
        if (this.hoverStateId === null) {
          map.setPaintProperty(layerId, 'fill-opacity', 1)
        }
      })

      /**
       * Hover - interaction
       */
      let clearHoverEffect = () => {
        if (this.activeStateId === null) {
          map.setPaintProperty(layerId, 'fill-opacity', 1)
        }
        this.hoverStateId = null
      }

      let hoverEffect = (e) => {
        if (e.features.length < 1) {
          return
        }

        clearHoverEffect()
        this.hoverStateId = e.features[0].properties[`${property}`]
        map.setPaintProperty(layerId, 'fill-opacity', [
          'case',
          ['boolean', [ '==', ['get', property], this.hoverStateId], false],
          1,
          ['boolean', [ '==', ['get', property], this.activeStateId], false],
          1,
          0.5
        ])
      }
      map.on('mouseenter', layerId, this.showPointer)
      map.on('mouseleave', layerId, this.hidePointer)
      map.on('mousemove', layerId, hoverEffect)
      map.on('mouseleave', layerId, clearHoverEffect)

      /**
       * Clean-up
       */
      this.$on('beforeDestroy', () => {
        map.off('click', layerId, this.showPopup)
        map.off('mouseenter', layerId, this.showPointer)
        map.off('mouseleave', layerId, this.hidePointer)
        map.off('mousemove', layerId, hoverEffect)
        map.off('mouseleave', layerId, clearHoverEffect)
      })
    },

    /**
     * FullScreen
     */
    handleFullscreenChange() {
      this.triggerRepaint()
    },
    triggerRepaint() {
      map.resize() // forces repaint
    },

    /**
     * Widget resize event
     *  Used to detect when responsive styles should go in effect.
     *  Regular media queries are of little use in a widget.
     */
    handleResize() {
      let mobileWidth = ContentConfigPerMap[this.activeMap] 
        ? ContentConfigPerMap[this.activeMap].popupWidth + 100 
        : 375

      this.mobile = this.$refs.widget.getBoundingClientRect().width < mobileWidth
    },

    /**
     * Legend
     */
    handleCloseLegend() {
      this.legendVisible = false
    },
    handleOpenLegend() {
      this.legendVisible = true
    },

    /**
     * Cursor change on hover
     */
    showPointer() {
      map.getCanvas().style.cursor = 'pointer'
    },
    hidePointer() {
      map.getCanvas().style.cursor = ''
    },

    /**
     * Popup
     * TODO: Connect to complete dataset
     */
    showPopup({ title, content, eventData }) {

      this.$emit('popup-open', { properties: eventData })
     
      this.popupTitle = title
      this.popupContent = content    
      this.popupVisible = true
      
      // Repaint after the animation, to fix the internal map dimensions
      if (this.repaintTimeout) {
        clearTimeout(this.repaintTimeout)
      }
      this.repaintTimeout = setTimeout(this.triggerRepaint, 300)
    },

    handleClosePopup() {
      this.$emit('popup-close')
      this.highlight = false
      this.popupVisible = false
      
      // Repaint after the animation, to fix the internal map dimensions
      if (this.repaintTimeout) {
        clearTimeout(this.repaintTimeout)
      }
      this.repaintTimeout = setTimeout(this.triggerRepaint, 300)
    }
  }
}
</script>

<style lang="scss">

map-widget {
  width: 100%;
}

#map-widget {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
  width: 100%;
  min-width: 100%;
  min-height: 100%;
  position: relative;
  overflow: hidden;
  height: 100%;

  *, :after, :before {
    box-sizing: border-box;
  }

  &.iframe {
    height: 100% !important;
    position: absolute;
    top: 0;
    left: 0; 
  }
  // .mobile {}
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0,0,0,0);
  white-space: nowrap;
  border: 0;
}

</style>
